import { useTranslation } from "react-i18next";
import {
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  MoreHorizontal,
} from "lucide-react";
import { Button } from "@/components/ui/Button";
import { Select } from "@/components/ui/Select";
import { cn } from "@/lib/cn";

type PageItem = number | "ellipsis-start" | "ellipsis-end";

function getPageItems(page: number, pageCount: number): PageItem[] {
  if (pageCount <= 7) return Array.from({ length: pageCount }, (_, index) => index + 1);

  const pages = new Set([1, pageCount, page - 1, page, page + 1]);
  const visible = [...pages]
    .filter((value) => value >= 1 && value <= pageCount)
    .sort((a, b) => a - b);
  const items: PageItem[] = [];

  for (const value of visible) {
    const previous = items.at(-1);
    if (typeof previous === "number" && value - previous > 1) {
      items.push(previous === 1 ? "ellipsis-start" : "ellipsis-end");
    }
    items.push(value);
  }
  return items;
}

interface PaginationProps {
  page: number;
  pageSize: number;
  total: number;
  pageSizeOptions?: readonly number[];
  disabled?: boolean;
  className?: string;
  onPageChange: (page: number) => void;
  onPageSizeChange: (pageSize: number) => void;
}

export function Pagination({
  page,
  pageSize,
  total,
  pageSizeOptions = [25, 50, 100],
  disabled = false,
  className,
  onPageChange,
  onPageSizeChange,
}: PaginationProps) {
  const { t } = useTranslation();
  const pageCount = Math.max(1, Math.ceil(total / pageSize));
  const safePage = Math.min(Math.max(1, page), pageCount);
  const from = total === 0 ? 0 : (safePage - 1) * pageSize + 1;
  const to = Math.min(safePage * pageSize, total);
  const canGoBack = safePage > 1 && !disabled;
  const canGoForward = safePage < pageCount && !disabled;

  return (
    <nav
      aria-label={t("pagination.label")}
      className={cn(
        "flex flex-col gap-3 rounded-md border border-line bg-surface px-3 py-3 sm:px-4",
        "lg:flex-row lg:items-center lg:justify-between",
        className,
      )}
    >
      <p className="font-mono tabular text-xs text-ink-soft">
        {t("pagination.range", { from, to, total })}
      </p>

      <div className="flex flex-wrap items-center gap-2 sm:flex-nowrap">
        <label className="flex items-center gap-2 text-xs text-ink-soft">
          <span className="whitespace-nowrap">{t("pagination.rowsPerPage")}</span>
          <Select
            value={pageSize}
            onChange={(event) => onPageSizeChange(Number(event.target.value))}
            disabled={disabled}
            aria-label={t("pagination.rowsPerPage")}
            containerClassName="h-8 w-[4.75rem]"
            className="font-mono tabular text-xs"
          >
            {pageSizeOptions.map((size) => <option key={size} value={size}>{size}</option>)}
          </Select>
        </label>

        <span className="mx-1 whitespace-nowrap font-mono tabular text-xs text-ink-faint">
          {t("pagination.pageOf", { page: safePage, pages: pageCount })}
        </span>

        <div className="flex items-center gap-1">
          <Button
            variant="secondary"
            size="icon-sm"
            disabled={!canGoBack}
            onClick={() => onPageChange(1)}
            aria-label={t("pagination.first")}
            title={t("pagination.first")}
          >
            <ChevronsLeft className="size-3.5" />
          </Button>
          <Button
            variant="secondary"
            size="icon-sm"
            disabled={!canGoBack}
            onClick={() => onPageChange(safePage - 1)}
            aria-label={t("pagination.previous")}
            title={t("pagination.previous")}
          >
            <ChevronLeft className="size-3.5" />
          </Button>

          <div className="hidden items-center gap-1 md:flex">
            {getPageItems(safePage, pageCount).map((item) =>
              typeof item === "number" ? (
                <Button
                  key={item}
                  variant={item === safePage ? "primary" : "secondary"}
                  size="icon-sm"
                  disabled={disabled}
                  onClick={() => onPageChange(item)}
                  aria-label={`${t("pagination.goToPage")} ${item}`}
                  aria-current={item === safePage ? "page" : undefined}
                  className="font-mono tabular"
                >
                  {item}
                </Button>
              ) : (
                <span key={item} className="grid size-7 place-items-center text-ink-faint" aria-hidden="true">
                  <MoreHorizontal className="size-4" />
                </span>
              ),
            )}
          </div>

          <Button
            variant="secondary"
            size="icon-sm"
            disabled={!canGoForward}
            onClick={() => onPageChange(safePage + 1)}
            aria-label={t("pagination.next")}
            title={t("pagination.next")}
          >
            <ChevronRight className="size-3.5" />
          </Button>
          <Button
            variant="secondary"
            size="icon-sm"
            disabled={!canGoForward}
            onClick={() => onPageChange(pageCount)}
            aria-label={t("pagination.last")}
            title={t("pagination.last")}
          >
            <ChevronsRight className="size-3.5" />
          </Button>
        </div>
      </div>
    </nav>
  );
}
